// TaskBoard.js
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import TaskColumn from './TaskColumn';
import { DragDropContext } from '@hello-pangea/dnd';
import axios from 'axios';
import api from '../../api';

const TaskBoard = () => {
  const [allStages, setAllStages] = useState([]);
  const [permittedStages, setPermittedStages] = useState([]);
  const [tasks, setTasks] = useState({});
  const { searchTerm = '', dateStart, dateEnd } = useOutletContext();
  const [roleId, setRoleId] = useState([]);
  const [organisation_id, setOrganisation_id] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userResponse = await api.get('/me', { withCredentials: true });
        if (!userResponse.data.success) {
          console.error('Impossible de récupérer les informations utilisateur.');
          return;
        }

        const user = userResponse.data.user;
        setOrganisation_id(parseInt(user.organisation_id, 10));
        setRoleId(parseInt(user.role_id, 10));
      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur.');
      }
    };

    fetchUser();
  }, []);

  // 1) Charger toutes les étapes
  useEffect(() => {
    api.get('/parametrages/etapes')
      .then(res => {
        const etapes = res.data.etapes || [];
        etapes.sort((a, b) => a.order - b.order);
        setAllStages(etapes);
      })
      .catch(err => console.error('GET /etapes error:', err));
  }, []);


  // 2) Étapes autorisées pour le rôle
  useEffect(() => {
    if (!roleId || allStages.length === 0) return;

    api.get(`/parametrages/roles/${roleId}`)
      .then(res => {
        const role = res.data.role || res.data;
        const allowedIds = Array.isArray(role.etapes) ? role.etapes.map(e => e.id) : [];
        const filtered = allStages
          .filter(stage => allowedIds.includes(stage.id))
          .sort((a, b) => a.order - b.order);             // <-- tri ici
        setPermittedStages(filtered);
      })
      .catch(err => console.error('Erreur GET /roles/{id} :', err));
  }, [roleId, allStages]);


  // 3) Charger et regrouper les tickets
  useEffect(() => {
    if (permittedStages.length === 0) return;

    const fetchTickets = () => {
      api.get(`/tickets/client/${organisation_id}`)
        .then(res => {
          const grouped = {};
          res.data.tickets.forEach(ticket => {

            // Condition corrigée : afficher les tickets avec les bonnes conditions
            const validationAdminCl = String(ticket.validation_adminCl);

            // Si c'est une demande : les 3 conditions doivent être vraies
            // Si c'est un bug : seulement validation_adminCl doit être "4"
            let isValidDemande = false;
            if (ticket.type === 'demande' && ticket.demande) {
              const confirmationAdmin = String(ticket.demande.confirmation_admin);
              const accepter = String(ticket.demande.accepter);
              isValidDemande = (confirmationAdmin === '1' && accepter === '1' && validationAdminCl === '4');
            }
            const isValidBug = (ticket.type === 'bug' && validationAdminCl === '4');

            if (!isValidDemande && !isValidBug) {
              return; // Ne pas afficher ce ticket
            }


            let rawId = ticket.etape_id;
            let stageId = typeof rawId === 'string' ? parseInt(rawId, 10) : rawId;
            const permittedIds = permittedStages.map(s => s.id);
            if (!permittedIds.includes(stageId)) {
              const idx = allStages.findIndex(s => s.id === stageId);
              const fallbackIdx = allStages
                .map((s, i) => (permittedIds.includes(s.id) ? i : -1))
                .filter(i => i < idx)
                .sort((a, b) => b - a)[0];
              stageId = (fallbackIdx != null && fallbackIdx >= 0)
                ? allStages[fallbackIdx].id
                : permittedIds[0];
            }
            if (!grouped[stageId]) grouped[stageId] = [];
            grouped[stageId].push({
              id: ticket.id,
              title: ticket.nom,
              content: ticket.content,
              type: ticket.type,
              deadlineClient: ticket.deadline_client,
              priorite: ticket.priorite,
              status: ticket.status,
              createur: ticket.createur ? `${ticket.createur.prenom} ${ticket.createur.nom}` : '-',
              etape: stageId,
            });
          });
          setTasks(grouped);
        })
        .catch(error => console.error('Erreur chargement tickets :', error));
    };
    fetchTickets();
    const interval = setInterval(fetchTickets, 5000);
    return () => clearInterval(interval);
  }, [permittedStages, allStages]);

  // 4) Drag & Drop
  const onDragEnd = result => {
    const { source, destination } = result;
    if (!destination) return;
    const src = parseInt(source.droppableId, 10);
    const dst = parseInt(destination.droppableId, 10);
    const srcList = Array.from(tasks[src] || []);
    const dstList = Array.from(tasks[dst] || []);
    const [moved] = srcList.splice(source.index, 1);
    moved.etape = dst;
    if (src === dst) {
      srcList.splice(destination.index, 0, moved);
      setTasks(prev => ({ ...prev, [src]: srcList }));
    } else {
      dstList.splice(destination.index, 0, moved);
      setTasks(prev => ({ ...prev, [src]: srcList, [dst]: dstList }));
    }
  };

  // 5) Filtrage par recherche et dates
  const filteredTasks = permittedStages.reduce((acc, stage) => {
    acc[stage.id] = (tasks[stage.id] || []).filter(task => {
      const textMatch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
      const taskDate = new Date(`${task.deadlineClient}T00:00:00`);
      let dateMatch = true;
      if (dateStart) dateMatch = dateMatch && taskDate >= new Date(`${dateStart}T00:00:00`);
      if (dateEnd) dateMatch = dateMatch && taskDate <= new Date(`${dateEnd}T23:59:59`);
      return textMatch && dateMatch;
    });
    return acc;
  }, {});

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className={`p-4 overflow-x-auto min-h-[calc(100vh-64px)] ${isMobile ? 'flex flex-col gap-3' : 'flex gap-4'}`}>
        {permittedStages.map(stage => (
          <TaskColumn
            key={stage.id}
            droppableId={String(stage.id)}
            title={stage.nom}
            count={filteredTasks[stage.id]?.length || 0}
            tasks={filteredTasks[stage.id] || []}
            couleur={stage.couleur}
            isMobile={isMobile}
          />
        ))}
      </div>
    </DragDropContext>
  );

};

export default TaskBoard;